import { Client } from 'src/api/Client';
import { 
  MediaTagsResponse, 
  ContentPlanItem,
  Thought,
  ThoughtsResponse,
  CreateThoughtPayload,
  UpdateThoughtPayload
} from './AiAgentClient.types';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
class AiAgentClient extends Client {
  private readonly Route = {
    MEDIA_TAGS: `${this.http.baseUrl}/ai-agent/media/tags`,
    CONTENT_PLAN: `${this.http.baseUrl}/ai-agent/content-ideas`,
    THOUGHTS_LIST: `${this.http.baseUrl}/thoughts`,
    THOUGHTS_CRUD: `${this.http.baseUrl}/thoughts`, // baseUrl already includes /api
  };

  private agentId: string | null = null;

  /**
   * Get the selected brand ID from cookie (same as BrandSelectionContext uses)
   */
  private getSelectedBrandId(): string | null {
    const BRAND_COOKIE_NAME = 'selectedBrandId';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${BRAND_COOKIE_NAME}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Gets the agent ID from localStorage
   * @returns Promise with the agent ID string
   */
  private async getAgentId(): Promise<string> {
    try {
      // If we already have an agent ID cached, return it
      if (this.agentId) {
        return this.agentId;
      }

      // Get from localStorage
      const cachedAgentId = localStorage.getItem('be_creatives_agent_id');
      if (cachedAgentId && cachedAgentId !== 'default-agent-id-for-testing') {
        this.agentId = cachedAgentId;
        return this.agentId;
      }

      // Development fallback - only use it if no cached ID is available
      if (process.env.NODE_ENV === 'development') {
        this.agentId = '60389db9-91dc-49de-aa85-7400c6a3ade6'; // Test agent ID
        return this.agentId;
      }

      throw new Error('No agent ID available. Please initialize the agent first.');
    } catch (error) {
      throw new Error('Failed to get agent ID. Please try again later.');
    }
  }

  /**
   * Get all available media tags
   * @returns Promise<MediaTagsResponse>
   */
  async getMediaTags(): Promise<MediaTagsResponse> {
    try {
      // Get agent_id for the request parameter
      const agentId = await this.getAgentId();
      
      // Build URL with agent_id parameter
      const url = `${this.Route.MEDIA_TAGS}?agent_id=${agentId}`;
      
      // Prepare headers with x-brand-id if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      // Add x-brand-id header if a brand is selected
      const selectedBrandId = this.getSelectedBrandId();
      if (selectedBrandId) {
        headers['x-brand-id'] = selectedBrandId;
      }
      
      // Add Authorization header
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      return this.http.get<MediaTagsResponse>(url, { headers }) as unknown as Promise<MediaTagsResponse>;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get content plan items
   * @returns Promise<ContentPlanItem[]>
   */
  async getContentPlan(): Promise<ContentPlanItem[]> {
    try {
      // Get agent_id for the request parameter
      const agentId = await this.getAgentId();
      
      // Build URL with agent_id parameter
      const url = `${this.Route.CONTENT_PLAN}?agent_id=${agentId}`;
      
      // Prepare headers with x-brand-id if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      // Add x-brand-id header if a brand is selected
      const selectedBrandId = this.getSelectedBrandId();
      if (selectedBrandId) {
        headers['x-brand-id'] = selectedBrandId;
      }
      
      // Add Authorization header
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      const response = await this.http.get(url, { headers }) as { data: ContentPlanItem[] };
      return response.data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all thoughts (ramblings) for the user
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise<ThoughtsResponse>
   */
  async getThoughts(page: number = 1, limit: number = 20, brandId?: string): Promise<ThoughtsResponse> {
    try {
      // Use brandId parameter if provided, otherwise get from cookie
      const selectedBrandId = brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }
      
      const url = `${this.Route.THOUGHTS_LIST}/${selectedBrandId}?page=${page}&limit=${limit}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      return this.http.get<ThoughtsResponse>(url, { headers }) as unknown as Promise<ThoughtsResponse>;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a single thought by session ID
   * @param sessionId - The session ID
   * @returns Promise<Thought>
   */
  async getThought(sessionId: string): Promise<Thought> {
    try {
      const selectedBrandId = this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }
      
      const url = `${this.Route.THOUGHTS_LIST}/${selectedBrandId}/${sessionId}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      const response = await this.http.get(url, { headers }) as { data: Thought };
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new thought
   * @param payload - The thought data
   * @returns Promise<Thought>
   */
  async createThought(payload: CreateThoughtPayload): Promise<Thought> {
    try {
      // Use brandId from payload if provided, otherwise get from cookie
      const selectedBrandId = payload.brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }
      
      const url = `${this.Route.THOUGHTS_LIST}/${selectedBrandId}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      // Remove brandId from payload before sending (it's in the URL)
      const { brandId: _, ...payloadWithoutBrandId } = payload;
      
      const response = await this.http.post(url, payloadWithoutBrandId, { headers }) as { data: Thought };
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a thought (rename)
   * @param sessionId - The session ID
   * @param payload - The update data
   * @param brandId - Optional brand ID
   * @returns Promise<Thought>
   */
  async updateThought(sessionId: string, payload: UpdateThoughtPayload, brandId?: string): Promise<Thought> {
    try {
      const selectedBrandId = brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }
      
      const url = `${this.Route.THOUGHTS_CRUD}/${selectedBrandId}/${sessionId}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      const response = await this.http.put(url, payload, { headers }) as Thought;
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a thought
   * @param sessionId - The session ID
   * @param brandId - Optional brand ID
   * @returns Promise<void>
   */
  async deleteThought(sessionId: string, brandId?: string): Promise<void> {
    try {
      const selectedBrandId = brandId || this.getSelectedBrandId();
      if (!selectedBrandId) {
        throw new Error('No brand selected');
      }
      
      const url = `${this.Route.THOUGHTS_CRUD}/${selectedBrandId}/${sessionId}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      const jwt = AuthUtils.getJwt();
      if (jwt?.token) {
        headers['Authorization'] = `Bearer ${jwt.token}`;
      }
      
      await this.http.delete(url, { headers });
    } catch (error) {
      throw error;
    }
  }
}

export { AiAgentClient };
